export type ToolStatus =
  'success' | 'error' | 'pending' | 'unknown' | 'stopped';

/**
 * Page-specific copy for `error` SSE events, keyed by the server's
 * `reason` discriminator. A reason with no entry falls back to the server's
 * own `error`/`detail` strings, so hosts only map the reasons they emit.
 */
export type ErrorCopy = Record<string, string>;

export type SseEvent =
  | {
      /**
       * Opening event of a run. `conversation_id` is the only key the shared
       * chat reads; hosts may emit additional page-scoped keys alongside it
       * (e.g. a workspace's `calculation_id`), which are ignored here.
       */
      event: 'meta';
      data: { conversation_id?: number } & Record<string, unknown>;
    }
  | {
      event: 'tool_call_start';
      data: { id: string; name: string; input: Record<string, unknown> };
    }
  | {
      event: 'tool_call_complete';
      data: {
        id: string;
        name: string | null;
        status: ToolStatus;
        duration_ms: number | null;
        output: unknown;
        error: string | null;
      };
    }
  | { event: 'assistant_text'; data: { text: string } }
  | { event: 'final'; data: { final_response: string | null } }
  | {
      event: 'error';
      data: { error: string; reason?: string; detail?: string };
    }
  | { event: 'done'; data: Record<string, never> };

/**
 * A persisted tool result, as stored in the `tool_results` jsonb column.
 *
 * These keys stay snake_case on the wire: Jbuilder camelizes the keys it
 * writes itself, but `deep_format_keys` is off, so the keys *inside* a jsonb
 * value pass through exactly as the model wrote them (see
 * AgentConversation#emit_tool_complete). The same shape arrives over SSE.
 */
export type ServerToolResult = {
  id: string;
  status: ToolStatus;
  output: unknown;
  error: string | null;
  duration_ms: number | null;
};

/**
 * Shapes returned by the JSON (Jbuilder) endpoints. Keys Jbuilder writes are
 * camelized app-wide by `Jbuilder.key_format(camelize: :lower)`, so these are
 * camelCase — unlike the SSE payloads above, which the controller serializes
 * straight from a Ruby hash and are therefore snake_case.
 */
export type ServerMessage = {
  id: number;
  role: 'user' | 'assistant';
  content: string | null;
  toolCalls: Array<{
    id: string;
    name: string;
    input: Record<string, unknown>;
  }> | null;
  toolResults: ServerToolResult[] | null;
  createdAt: string;
};

export type IndexResponse = {
  conversationId: number | null;
  messages: ServerMessage[];
};

/**
 * The record a page's conversations belong to. Without it every document would
 * share one thread per agent, so opening the panel on a new document would show
 * the previous one's transcript.
 */
export type AgentSubject = {
  /** Model name as the server records it, e.g. "Document". */
  type: string;
  id: number | string;
};

// Namespaces per-conversation client state by agent *and* record.
export const agentScopeKey = (
  agentType: string,
  subject?: AgentSubject | null
) => (subject ? `${agentType}:${subject.type}:${subject.id}` : agentType);

export type ConversationListItem = {
  id: number;
  title: string | null;
  status: 'active' | 'completed' | 'errored';
  updatedAt: string;
  createdAt: string;
};

export type Starter = {
  /** Short label on the home-screen button. */
  title: string;
  /** Full prompt sent in place of the label (see the PRE_SEND swap). */
  prompt: string;
};
